import { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import type { AboutMediaItem } from "./about-media";
import AboutMediaFrame from "./AboutMediaFrame";
import AboutMediaCarouselLightbox from "./AboutMediaCarouselLightbox";

export type FeatureCarouselSlide = {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  media: AboutMediaItem;
};

type FeatureCarouselProps = {
  slides: readonly FeatureCarouselSlide[];
};

export default function FeatureCarousel({ slides }: FeatureCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const reduceMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const mediaItems = slides.map((slide) => slide.media);

  const scrollToIndex = useCallback(
    (index: number) => {
      const track = trackRef.current;
      if (!track) return;
      const slide = track.children[index] as HTMLElement | undefined;
      if (!slide) return;
      slide.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "nearest",
        inline: "center",
      });
      setActiveIndex(index);
    },
    [reduceMotion],
  );

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const onScroll = () => {
      const { scrollLeft, clientWidth } = track;
      const index = Math.round(scrollLeft / clientWidth);
      setActiveIndex(Math.min(Math.max(index, 0), slides.length - 1));
    };

    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, [slides.length]);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const goPrev = () => scrollToIndex(Math.max(activeIndex - 1, 0));
  const goNext = () => scrollToIndex(Math.min(activeIndex + 1, slides.length - 1));

  return (
    <>
      <div className="about-carousel">
        <div className="about-carousel-controls" aria-hidden={slides.length <= 1}>
          <button
            type="button"
            className="about-carousel-arrow"
            onClick={goPrev}
            disabled={activeIndex === 0}
            aria-label="Previous feature"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button
            type="button"
            className="about-carousel-arrow"
            onClick={goNext}
            disabled={activeIndex === slides.length - 1}
            aria-label="Next feature"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>

        <div
          ref={trackRef}
          className="about-carousel-track"
          role="region"
          aria-roledescription="carousel"
          aria-label="Feature highlights"
        >
          {slides.map((slide, index) => (
            <article
              key={slide.id}
              className="about-carousel-slide"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${slides.length}: ${slide.title}`}
              aria-hidden={index !== activeIndex}
            >
              <div className="about-carousel-slide-copy">
                <p className="eyebrow">{slide.eyebrow}</p>
                <h3 className="about-carousel-slide-title">{slide.title}</h3>
                <p className="about-carousel-slide-body">{slide.body}</p>
              </div>
              <AboutMediaFrame
                item={slide.media}
                className="about-carousel-slide-shot"
                onClick={() => openLightbox(index)}
              />
            </article>
          ))}
        </div>

        <div className="about-carousel-dots" role="tablist" aria-label="Choose a feature">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              role="tab"
              className="about-carousel-dot"
              aria-selected={index === activeIndex}
              aria-label={slide.title}
              onClick={() => scrollToIndex(index)}
            />
          ))}
        </div>
      </div>

      <AboutMediaCarouselLightbox
        items={mediaItems}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}
